// Finance — Purchase Orders register.
//
// A dedicated PO surface the President asked for: a searchable list of ALL POs
// (open + closed, FY22 through the latest register-feed refresh), the live
// OPERATING-FUND open-encumbrance KPIs (fund-2101 carryover reported
// separately), where it's encumbered (by object + by committee), and the
// monthly PO-raised cadence (value/month + cumulative). Levy fund (PWC
// appropriation) — the same Mobius source as the Levy page. NOTE: the register
// table (dash_finance_po) refreshes on its own cadence and can trail the live
// KPIs at the FY boundary — register sections are labeled with the register
// FY, KPI sections with the served operating FY/fund; never cross-label.
//
// Filtering + pagination are SERVER-SIDE (/api/finance/po-register); the FY
// defaults to the current FY derived server-side from the clock (never hardcoded
// 'FY26' — FY27 starts 7/1).

import { useMemo, useState, useRef } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Search, CalendarClock } from "lucide-react";
import {
  usePoSummary,
  usePoRegister,
  usePoCommittees,
  useFinanceOpenPos,
  type PoRegisterParams,
  type PoRegisterRow,
  type OpenPoRow,
} from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import FundBadge from "../components/finance/FundBadge";
import FreshnessStrip from "../components/finance/FreshnessStrip";
import ExportCsvButton from "../components/finance/ExportCsvButton";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import InfoTip from "../components/shared/InfoTip";
import PageIntro, { type VerdictTone } from "../components/shared/PageIntro";
import type { CsvColumn } from "../lib/csv";
import {
  fiscalCloseDeadlines,
  currentFiscalYear,
  fundForFy,
  priorFiscalYear,
} from "../lib/fiscal";
import {
  asNum,
  usd0,
  usdC,
  TOOLTIP_PROPS,
  ACCENT,
  MUTED,
  GRID,
  COLORS,
} from "../components/finance/charts";
import {
  TH as TH_BASE,
  THR as THR_BASE,
  TD,
  TDR,
  PILL_ACTIVE,
  FetchErrorBanner,
} from "../components/finance/ui";

// This page's PO tables keep nowrap headers (long object descriptions);
// extend the shared TH/THR with whitespace-nowrap.
const TH = `${TH_BASE} whitespace-nowrap`;
const THR = `${THR_BASE} whitespace-nowrap`;

// FY26 → "Jul '25" etc. The FY label is the year the FY ends, so a 'YYYY-MM'
// month maps cleanly to a short month label.
function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const idx = Number(m) - 1;
  return `${names[idx] ?? m} '${y.slice(2)}`;
}

const STATUS_OPTIONS: { key: "all" | "open" | "closed"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "closed", label: "Closed" },
];

function statusBadge(status: string | null) {
  const isOpen = status === "Open";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
        isOpen
          ? "bg-status-green/15 text-status-green"
          : "bg-theme-overlay text-theme-muted"
      }`}
    >
      {status ?? "—"}
    </span>
  );
}

// ── CSV column specs ──────────────────────────────────────────────────────────
// Register export: the rows the page currently has loaded (current filters +
// page). Gross PO is the header commitment; Remaining is the open encumbrance.
const REGISTER_CSV_COLUMNS: CsvColumn<PoRegisterRow>[] = [
  { key: "po_number", header: "PO #" },
  { key: "supplier", header: "Supplier" },
  { key: "description", header: "Description" },
  { key: "committee", header: "Committee" },
  { key: "object_segment", header: "Object" },
  { key: "object_desc", header: "Object Description" },
  { key: "total", header: "Gross PO", format: (v) => Math.round(asNum(v)) },
  {
    key: "remaining",
    header: "Remaining (open encumbrance)",
    format: (v) => Math.round(asNum(v)),
  },
  { key: "status", header: "Status" },
  { key: "order_date", header: "Order Date" },
  { key: "fiscal_year", header: "FY" },
  { key: "is_capital", header: "Capital", format: (v) => (v ? "Yes" : "No") },
  { key: "is_reissue", header: "Re-issued", format: (v) => (v ? "Yes" : "No") },
];

// Close-worklist export: open POs (remaining > 0) keyed for the run-down.
// Fund is exported so the operating-vs-2101-carryover pools stay separable.
const WORKLIST_CSV_COLUMNS: CsvColumn<OpenPoRow>[] = [
  { key: "po_number", header: "PO #" },
  { key: "fund", header: "Fund" },
  {
    key: "remaining",
    header: "Remaining",
    format: (v) => Math.round(asNum(v)),
  },
  { key: "supplier", header: "Supplier" },
  { key: "object_segment", header: "Object" },
  { key: "object_desc", header: "Object Description" },
  { key: "budget_date", header: "Budget Date" },
  { key: "status", header: "Status" },
  {
    key: "carryover",
    header: "Carryover / Current FY",
    format: (v) => (asNum(v) === 1 ? "Carryover" : "Current FY"),
  },
  {
    key: "fy_established",
    header: "FY Established",
    format: (v) => (v ? `FY${String(v).slice(2)}` : ""),
  },
];

// ── Countdown banner: days to fiscal close (Jun 30) + the PWC year-end P-Card
// allocation deadline (Jun 24 — a fixed county calendar date, NOT tied to any
// per-PO flag). Dates are FY-derived (lib/fiscal) — roll forward each year.
// Far from close (> 60 days out) it drops to neutral styling with softer copy:
// an amber alert 300+ days from June 30 reads as false urgency.
function CloseCountdownBanner() {
  const d = fiscalCloseDeadlines();
  // > 60 days out there is no year-end urgency — informational, not an alert.
  const urgent = d.daysToClose <= 60;
  const fmt = (iso: string) =>
    new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  const dayWord = (n: number) =>
    n < 0 ? `${Math.abs(n)} days past` : `${n} day${n === 1 ? "" : "s"}`;
  const closeTone = !urgent
    ? "text-theme-primary"
    : d.daysToClose <= 7
      ? "text-status-red"
      : "text-status-amber";
  const pcardTone = !urgent
    ? "text-theme-primary"
    : d.daysToPcard <= 3
      ? "text-status-red"
      : "text-status-amber";
  const iconTone = urgent ? "text-status-amber" : "text-theme-muted";
  return (
    <div
      className={`glass rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 border-l-4 ${
        urgent ? "border-status-amber" : "border-theme-subtle"
      }`}
    >
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className={`${iconTone} shrink-0`} />
        <div>
          <span className={`text-lg font-bold tabular-nums ${closeTone}`}>
            {dayWord(d.daysToClose)}
          </span>{" "}
          <span className="text-sm text-theme-secondary">
            to fiscal close ({fmt(d.fiscalCloseDate)})
            {!urgent && " — no year-end urgency yet"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:border-l sm:border-theme-subtle sm:pl-6">
        <CalendarClock size={18} className={`${iconTone} shrink-0`} />
        <div>
          <span className={`text-lg font-bold tabular-nums ${pcardTone}`}>
            {dayWord(d.daysToPcard)}
          </span>{" "}
          <span className="text-sm text-theme-secondary">
            to year-end P-Card allocation deadline ({fmt(d.pcardDate)})
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Lead KPI card: OPERATING-FUND Open Encumbrance with the current-FY vs
// carryover breakout. This is the metric the President asked to polish — one
// headline number with the FY/carryover split shown as a subheading in the same
// graphic. The headline is the operating fund ONLY (Oracle-tied); prior-fund
// 2101 carryover POs ride their own swept appropriation and are shown as a
// separate labeled line, never blended into the headline.
function EncumbranceLeadCard({
  total,
  openCount,
  currentFyEnc,
  currentFyCount,
  carryoverEnc,
  carryoverCount,
  carry2101Enc,
  carry2101Count,
  operatingFund,
  operatingFy,
  priorFyLabel,
  isLoading,
}: {
  total: number;
  openCount: number;
  currentFyEnc: number;
  currentFyCount: number;
  carryoverEnc: number;
  carryoverCount: number;
  carry2101Enc: number;
  carry2101Count: number;
  operatingFund: string;
  operatingFy: string;
  priorFyLabel: string;
  isLoading: boolean;
}) {
  const poLabel = (n: number) => `${n} PO${n === 1 ? "" : "s"}`;
  return (
    <div className="glass rounded-2xl p-5 flex flex-col gap-3 relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-status-amber" />
      <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider leading-none pt-1 inline-flex items-center gap-1">
        Open Encumbrance <InfoTip term="encumbrance" />
      </p>
      {isLoading ? (
        <div className="skeleton h-10 w-40 rounded-md" />
      ) : (
        <p className="text-4xl font-bold leading-none tabular-nums text-status-amber">
          {usd0(total)}
        </p>
      )}
      <p className="text-xs text-theme-muted leading-snug">
        Remaining on {poLabel(openCount)} open · {operatingFy} operating fund{" "}
        {operatingFund} (live)
      </p>

      {/* Polished current-FY vs carryover breakout */}
      {isLoading ? (
        <div className="skeleton h-12 w-full rounded-md" />
      ) : (
        <div className="mt-1 grid grid-cols-2 gap-2 border-t border-theme-subtle/50 pt-3">
          <div>
            <p className="text-[10px] font-semibold text-theme-muted uppercase tracking-wider">
              Current FY
            </p>
            <p className="text-base font-semibold tabular-nums text-theme-primary leading-tight">
              {usd0(currentFyEnc)}
            </p>
            <p className="text-[11px] text-theme-muted">
              {poLabel(currentFyCount)}
            </p>
          </div>
          <div className="border-l border-theme-subtle pl-2">
            <p className="text-[10px] font-semibold text-theme-muted uppercase tracking-wider inline-flex items-center gap-1">
              Carryover <InfoTip term="carryover" size={11} />
            </p>
            <p className="text-base font-semibold tabular-nums text-theme-primary leading-tight">
              {usd0(carryoverEnc)}
            </p>
            <p className="text-[11px] text-theme-muted">
              {poLabel(carryoverCount)} · carried onto fund {operatingFund}
            </p>
          </div>
        </div>
      )}

      {/* Prior-fund 2101 carryover — its OWN swept appropriation, NOT in the
          headline above. Labeled so nobody adds it into the operating figure. */}
      {!isLoading && carry2101Enc > 0 && (
        <p className="text-xs text-theme-secondary leading-snug border-t border-theme-subtle/50 pt-2 tabular-nums">
          + {usd0(carry2101Enc)} on {poLabel(carry2101Count)} {priorFyLabel}
          -carryover (fund 2101 — own swept appropriation, not included above)
        </p>
      )}
    </div>
  );
}

export default function FinancePurchaseOrders() {
  // Top-level view: the full Register, or the FY-close "Close Worklist".
  const [view, setView] = useState<"register" | "worklist">("register");

  // Filter state. fy '' = use the server's current-FY default; 'all' = every FY.
  const [fy, setFy] = useState<string>("");
  const [status, setStatus] = useState<"all" | "open" | "closed">("all");
  const [committee, setCommittee] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<"order_date" | "total">("order_date");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  // Summary tracks whichever explicit FY is chosen; '' / 'all' → server current FY.
  const summaryFy = fy && fy !== "all" ? fy : undefined;
  const {
    data: summary,
    isLoading: sumLoading,
    error: summaryError,
  } = usePoSummary(summaryFy);
  const { data: committees } = usePoCommittees();

  const registerParams: PoRegisterParams = useMemo(
    () => ({
      fy: fy || undefined,
      status,
      committee: committee || undefined,
      q: q || undefined,
      page,
      sort,
      dir,
    }),
    [fy, status, committee, q, page, sort, dir],
  );
  const { data: register, isLoading: regLoading } =
    usePoRegister(registerParams);

  // ── Close worklist: open POs (remaining > 0), straight from the validated
  // open-encumbrance source (dash_finance_open_po — the same feed behind the
  // Open Encumbrance KPI). Limit 500 covers the full open set. Sorted client-side.
  const { data: openPos, isLoading: worklistLoading } = useFinanceOpenPos();
  const [wlSort, setWlSort] = useState<"remaining" | "budget_date">(
    "remaining",
  );
  const [wlDir, setWlDir] = useState<"asc" | "desc">("desc");
  const worklistRows = useMemo(() => {
    const rs = [...(openPos?.data ?? [])];
    rs.sort((a, b) => {
      let cmp: number;
      if (wlSort === "remaining") {
        cmp = asNum(a.remaining) - asNum(b.remaining);
      } else {
        cmp = String(a.budget_date ?? "").localeCompare(
          String(b.budget_date ?? ""),
        );
      }
      return wlDir === "asc" ? cmp : -cmp;
    });
    return rs;
  }, [openPos, wlSort, wlDir]);
  function toggleWlSort(col: "remaining" | "budget_date") {
    if (wlSort === col) setWlDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setWlSort(col);
      setWlDir(col === "remaining" ? "desc" : "asc");
    }
  }
  const wlArrow = (col: "remaining" | "budget_date") =>
    wlSort === col ? (wlDir === "asc" ? " ▲" : " ▼") : "";

  // REGISTER FY actually shown. This can lag the clock while the register feed
  // refresh is pending — it must only ever label the register/cadence sections,
  // never the live KPI block.
  const currentFy = summary?.fiscal_year ?? "current FY";
  // Fund + FY of the LIVE operating/encumbrance KPI block, as SERVED
  // (summary.operating_fund / operating_fy). Never derive these from the
  // register's fallback FY — that printed "Fund 2101-65-6560" under live
  // FY27/2119 numbers when the register lagged at the FY boundary. Clock-FY
  // fund only as a loading fallback.
  const opFy = summary?.operating_fy ?? currentFiscalYear();
  const opFund = summary?.operating_fund ?? fundForFy(currentFiscalYear());
  const priorFyLabel = priorFiscalYear(opFy);
  // The register endpoint falls back to the latest FY with rows when the
  // requested (clock) FY has none — e.g. while the ~weekly register feed
  // refresh is pending after 7/1. Surface that so an empty new-year register
  // isn't read as "no PO activity" — but never claim the county hasn't loaded
  // the new year (the live KPIs above the register prove otherwise).
  const isFyFallback = !!summary?.is_fallback;
  const requestedFy = summary?.requested_fy ?? currentFiscalYear();
  const kpis = summary?.kpis;

  // ── Worklist fund split: the /api/finance/open-pos rows span BOTH pools in
  // an FY27-era view — operating fund 2119 POs (the pool the Open Encumbrance
  // KPI measures) AND fund-2101 carryover POs riding their own swept
  // appropriation. Subtotal them separately; never blend. In FY26-era views
  // the operating fund IS 2101 and there is no separate carryover pool.
  const wlIsCarryPool = (r: OpenPoRow) =>
    opFund !== "2101" && r.fund === "2101";
  const wlOperatingRows = useMemo(
    () => worklistRows.filter((r) => !(opFund !== "2101" && r.fund === "2101")),
    [worklistRows, opFund],
  );
  const wlOperatingTotal = wlOperatingRows.reduce(
    (s, r) => s + asNum(r.remaining),
    0,
  );
  const wlCarryRows = worklistRows.filter(wlIsCarryPool);
  const wlCarryTotal = wlCarryRows.reduce((s, r) => s + asNum(r.remaining), 0);

  // FY-filter options, derived live instead of a hardcoded list so FY27 appears
  // automatically on rollover. The register is server-filtered by FY, so a
  // single-FY selection only returns that year — we therefore ACCUMULATE every
  // distinct fiscal_year we've seen (across renders) and union in the server's
  // current FY plus the clock-derived current FY, so the dropdown never loses
  // years and always offers the new one. Sorted newest-first.
  const fyOptionsRef = useRef<Set<string>>(new Set());
  // Register-only FY set: the years ACTUALLY present in register data. The
  // header's range label derives from this (not from the dropdown set, which
  // unions in the clock FY) so the subtitle never advertises a year the
  // register doesn't hold yet — e.g. "…–FY27" while the feed refresh is pending.
  const registerFyRef = useRef<Set<string>>(new Set());
  for (const r of register?.data ?? []) {
    if (r.fiscal_year && /^FY\d{2}$/.test(r.fiscal_year)) {
      fyOptionsRef.current.add(r.fiscal_year);
      registerFyRef.current.add(r.fiscal_year);
    }
  }
  if (summary?.fiscal_year && /^FY\d{2}$/.test(summary.fiscal_year)) {
    // summary.fiscal_year is the register-resolved FY (fallback-aware), so it
    // belongs in both sets.
    fyOptionsRef.current.add(summary.fiscal_year);
    registerFyRef.current.add(summary.fiscal_year);
  }
  fyOptionsRef.current.add(currentFiscalYear());
  // Ensure the currently-selected FY is always present as an option.
  if (fy && fy !== "all" && /^FY\d{2}$/.test(fy)) fyOptionsRef.current.add(fy);
  // Cheap to recompute each render (a handful of FY labels); newest-first.
  const fyOptions = [...fyOptionsRef.current].sort(
    (a, b) => Number(b.slice(2)) - Number(a.slice(2)),
  );
  // Human range label for the register span, e.g. "FY22–FY26" — derived ONLY
  // from FYs seen in register data (newest-first) so it widens when new years
  // actually land in the feed.
  const registerFys = [...registerFyRef.current].sort(
    (a, b) => Number(b.slice(2)) - Number(a.slice(2)),
  );
  const fyRangeLabel =
    registerFys.length > 1
      ? `${registerFys[registerFys.length - 1]}–${registerFys[0]}`
      : (registerFys[0] ?? currentFy);

  // ── Verdict banner — derived live: open encumbrance to run down, how much is
  // carryover, and days to fiscal close. Tone keys off the close countdown
  // (more urgent as June 30 approaches with money still open).
  const { daysToClose } = fiscalCloseDeadlines();
  // Headline = OPERATING FUND ONLY (Oracle-tied). The fund-2101 carryover slice
  // rides its own swept appropriation and is stated as a separate labeled
  // addendum — never folded into the headline sentence's figure.
  const openEnc = asNum(kpis?.open_encumbrance);
  const openCount = asNum(kpis?.open_po_count);
  const carryEnc = asNum(kpis?.carryover_encumbrance);
  const carry2101Enc = asNum(kpis?.carryover_2101_encumbrance);
  const carry2101Count = asNum(kpis?.carryover_2101_count);
  const verdictReady = !sumLoading && kpis != null;
  const verdictTone: VerdictTone =
    daysToClose <= 7 && openEnc > 0
      ? "red"
      : daysToClose <= 30 && openEnc > 0
        ? "amber"
        : "green";
  const daysPhrase =
    daysToClose >= 0
      ? `${daysToClose} day${daysToClose === 1 ? "" : "s"} to fiscal close (June 30)`
      : "the fiscal year is closed";
  const carry2101Phrase =
    carry2101Enc > 0
      ? ` Separately, ${usd0(carry2101Enc)} remains on ${carry2101Count} ${priorFyLabel}-carryover PO${carry2101Count === 1 ? "" : "s"} (fund 2101 — own swept appropriation, not in the headline).`
      : "";
  const verdictText =
    openEnc > 0
      ? `${usd0(openEnc)} is still committed on ${openCount} open PO${openCount === 1 ? "" : "s"} on the ${opFy} operating fund ${opFund} (${usd0(carryEnc)} of it carried onto ${opFund} from prior years) — ${daysPhrase}.${carry2101Phrase} Finance is working the close worklist.`
      : `No open purchase orders to run down on the ${opFy} operating fund — ${daysPhrase}.${carry2101Phrase}`;

  // ── monthly trend chart data ──
  // Lead on PO COUNT (unambiguous); the dollar series is GROSS COMMITMENTS (not
  // spend) and is labeled as such. Duplicate re-issue copies are already excluded
  // server-side.
  const trendData = (summary?.monthly_trend ?? []).map((r) => ({
    name: monthLabel(r.month),
    "POs raised": asNum(r.po_count),
    "Gross commitments": Math.round(asNum(r.value)),
  }));

  // ── encumbrance-by-object chart data (top 10) ──
  const byObject = (summary?.encumbrance_by_object ?? [])
    .slice(0, 10)
    .map((r) => ({
      name:
        r.object_desc.length > 28
          ? r.object_desc.slice(0, 27) + "…"
          : r.object_desc,
      full: r.object_desc,
      enc: Math.round(asNum(r.encumbered)),
      count: asNum(r.po_count),
    }));
  const byCommittee = summary?.encumbrance_by_committee ?? [];

  const totalRows = register?.total ?? 0;
  const totalPages = register?.total_pages ?? 1;
  const rows = register?.data ?? [];

  // A filter change resets to page 1; sort toggles stay on the current sort col.
  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }
  function toggleSort(col: "order_date" | "total") {
    if (sort === col) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(col);
      setDir("desc");
    }
    setPage(1);
  }
  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(qInput.trim());
    setPage(1);
  }

  const sortArrow = (col: "order_date" | "total") =>
    sort === col ? (dir === "asc" ? " ▲" : " ▼") : "";

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Purchase Orders"
          subtitle={`Levy fund (PWC appropriation) · KPIs live ${opFy} (fund ${opFund}) · PO register ${fyRangeLabel} · register showing ${currentFy}`}
          headerRight={<FundBadge kind="levy" />}
        />
        {verdictReady && (
          <PageIntro tone={verdictTone}>{verdictText}</PageIntro>
        )}
        <FreshnessStrip className="mt-3" sources={["open_po"]} />
        {isFyFallback && (
          <div
            role="alert"
            className="mt-3 flex items-start gap-3 rounded-lg border px-4 py-3 bg-status-amber/12 border-status-amber/40 text-status-amber"
          >
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="text-sm">
              {/* The county HAS loaded the new year — the live KPIs above prove
                  it. Only the ~weekly PO-register feed is behind; never phrase
                  this as "the county hasn't loaded {requestedFy}". Register
                  vintage: frozen ~Jul 6, 2026 pending the Wave-B refresh — the
                  banner disappears once {requestedFy} rows land in the feed. */}
              <p className="font-semibold">
                The PO register below still shows {currentFy} — its data feed
                refresh is pending (register as of ~Jul 6).
              </p>
              <p className="mt-1 text-theme-muted">
                {requestedFy} is live: the Open Encumbrance and Operating KPIs
                above are current {opFy} (fund {opFund}) figures. Only the
                register table, cadence, and gross-commitment sections below are{" "}
                {currentFy}; new {requestedFy} rows (with supplier/committee
                attribution) appear with the next PO-register refresh.
              </p>
            </div>
          </div>
        )}
      </div>

      <FetchErrorBanner error={summaryError} what="purchase orders" />

      {/* View toggle: full Register vs the FY-close Worklist */}
      <div className="flex items-center gap-1 -mt-2">
        {(
          [
            { key: "register", label: "Register" },
            { key: "worklist", label: "Close Worklist" },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === key
                ? PILL_ACTIVE
                : "bg-theme-overlay text-theme-muted hover:text-theme-primary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Lead KPI: Open Encumbrance (the large-number graphic), polished with
          a current-FY vs carryover breakout per the President's directive. ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <EncumbranceLeadCard
            total={asNum(kpis?.open_encumbrance)}
            openCount={asNum(kpis?.open_po_count)}
            currentFyEnc={asNum(kpis?.current_fy_encumbrance)}
            currentFyCount={asNum(kpis?.current_fy_count)}
            carryoverEnc={asNum(kpis?.carryover_encumbrance)}
            carryoverCount={asNum(kpis?.carryover_count)}
            carry2101Enc={carry2101Enc}
            carry2101Count={carry2101Count}
            operatingFund={opFund}
            operatingFy={opFy}
            priorFyLabel={priorFyLabel}
            isLoading={sumLoading}
          />
        </div>

        {/* Supporting validated KPIs — labeled with the SERVED operating fund/FY
            (never the register's fallback FY), ties to Oracle. */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <KPICard
            label="Operating Spent"
            value={usd0(asNum(kpis?.operating_spent))}
            subtitle={`${opFy} fund ${opFund} operating · ties to Oracle · ${asNum(kpis?.operating_pct)}% of budget`}
            accent="neutral"
            isLoading={sumLoading}
          />
          <KPICard
            label="Operating Budget"
            value={usd0(asNum(kpis?.operating_budget))}
            subtitle={`${opFy} fund ${opFund} · county base appropriation (incl. reserve) · ties to Oracle`}
            accent="neutral"
            isLoading={sumLoading}
          />
        </div>
      </div>

      {view === "worklist" && (
        <CloseWorklist
          rows={worklistRows}
          isLoading={worklistLoading}
          operatingTotal={wlOperatingTotal}
          operatingCount={wlOperatingRows.length}
          carryTotal={wlCarryTotal}
          carryCount={wlCarryRows.length}
          kpiTotal={asNum(kpis?.open_encumbrance)}
          operatingFund={opFund}
          priorFyLabel={priorFyLabel}
          registerPending={isFyFallback}
          newFySuffix={opFy.replace(/^FY/, "")}
          onSort={toggleWlSort}
          arrow={wlArrow}
        />
      )}

      {view === "register" && (
        <>
          {/* Where it's encumbered — by object + by committee */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Where It's Encumbered — by Object"
              subtitle="Current open-PO remaining, grouped by spend object (top 10)"
              empty={!sumLoading && byObject.length === 0}
              minHeight={120}
            >
              {byObject.length > 0 && (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(220, byObject.length * 32)}
                >
                  <BarChart
                    data={byObject}
                    layout="vertical"
                    margin={{ top: 4, right: 56, bottom: 4, left: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID}
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => usdC(Number(v))}
                      tick={{ fontSize: 11, fill: MUTED }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={170}
                      tick={{ fontSize: 10, fill: MUTED }}
                    />
                    <Tooltip
                      formatter={(v) => [usd0(Number(v)), "Encumbered"]}
                      labelFormatter={(_, p) =>
                        (p?.[0]?.payload as { full?: string })?.full ?? ""
                      }
                      {...TOOLTIP_PROPS}
                    />
                    <Bar
                      dataKey="enc"
                      fill={COLORS[3]}
                      radius={[0, 3, 3, 0]}
                      maxBarSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              title="Where It's Encumbered — by Committee"
              subtitle="Current open-PO remaining, attributed by committee"
              empty={!sumLoading && byCommittee.length === 0}
              minHeight={120}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <caption className="sr-only">
                    Open encumbrance by committee
                  </caption>
                  <thead>
                    <tr className="border-b border-theme-subtle">
                      <th scope="col" className={TH}>
                        Committee
                      </th>
                      <th scope="col" className={THR}>
                        Open POs
                      </th>
                      <th scope="col" className={THR}>
                        Encumbered
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCommittee.map((r, idx) => (
                      <tr
                        key={r.committee}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className={`${TD} flex items-center gap-2`}>
                          <span
                            className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{ background: COLORS[idx % COLORS.length] }}
                          />
                          {r.committee}
                        </td>
                        <td className={TDR}>{asNum(r.po_count)}</td>
                        <td
                          className={`${TDR} font-semibold text-theme-primary`}
                        >
                          {usd0(asNum(r.encumbered))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>

          {/* Monthly PO cadence (current FY): PO COUNT/month (primary) + gross
          commitment dollars (secondary, labeled — NOT spend). */}
          <ChartCard
            title={`PO Cadence — ${currentFy}`}
            subtitle="Purchase orders raised by order-month (count). Dollar line is gross commitments (incl. capital) — not operating spend."
            empty={!sumLoading && trendData.length === 0}
            minHeight={120}
          >
            {trendData.length > 0 && (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  data={trendData}
                  margin={{ top: 8, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: MUTED }} />
                  <YAxis
                    yAxisId="left"
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: MUTED }}
                    width={36}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tickFormatter={(v) => usdC(Number(v))}
                    tick={{ fontSize: 11, fill: MUTED }}
                    width={56}
                  />
                  <Tooltip
                    formatter={(v, k) =>
                      k === "Gross commitments"
                        ? [usd0(Number(v)), k as string]
                        : [Number(v), k as string]
                    }
                    {...TOOLTIP_PROPS}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    yAxisId="left"
                    dataKey="POs raised"
                    fill={ACCENT}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={36}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="Gross commitments"
                    stroke={COLORS[2]}
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Register — searchable / filterable / paginated */}
          <ChartCard
            title="PO Register"
            subtitle={`${totalRows.toLocaleString()} purchase order${totalRows === 1 ? "" : "s"} match the current filters · CSV exports the ${rows.length} loaded row${rows.length === 1 ? "" : "s"} on this page`}
            minHeight={120}
            headerRight={
              <ExportCsvButton
                filename={`dcvfd-po-register-${currentFy}`}
                rows={rows}
                columns={REGISTER_CSV_COLUMNS}
              />
            }
          >
            {/* Filter bar */}
            <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
              <form onSubmit={submitSearch} className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Search
                </label>
                <div className="relative mt-1">
                  <Search
                    size={15}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-muted"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    placeholder="PO #, supplier, or description…"
                    className="w-full pl-8 pr-3 py-2 rounded-lg bg-theme-overlay border border-theme-subtle text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
                  />
                </div>
              </form>

              <div>
                <label className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Fiscal Year
                </label>
                <select
                  value={fy}
                  onChange={(e) => resetPage(setFy)(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 rounded-lg bg-theme-overlay border border-theme-subtle text-sm text-theme-primary focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
                >
                  <option value="">Current ({currentFy})</option>
                  <option value="all">All years</option>
                  {fyOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Status
                </label>
                <div className="mt-1 flex items-center gap-1">
                  {STATUS_OPTIONS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => resetPage(setStatus)(key)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        status === key
                          ? PILL_ACTIVE
                          : "bg-theme-overlay text-theme-muted hover:text-theme-primary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Committee
                </label>
                <select
                  value={committee}
                  onChange={(e) => resetPage(setCommittee)(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 rounded-lg bg-theme-overlay border border-theme-subtle text-sm text-theme-primary focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
                >
                  <option value="">All committees</option>
                  {(committees?.committees ?? []).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto -mx-4 md:-mx-5">
              <table className="w-full min-w-[1000px] text-left">
                <caption className="sr-only">Purchase order register</caption>
                <thead>
                  <tr className="border-b border-theme-subtle">
                    <th scope="col" className={TH}>
                      PO #
                    </th>
                    <th scope="col" className={TH}>
                      Supplier
                    </th>
                    <th scope="col" className={TH}>
                      Description
                    </th>
                    <th scope="col" className={TH}>
                      Committee
                    </th>
                    <th scope="col" className={TH}>
                      Object
                    </th>
                    <th
                      scope="col"
                      className={`${THR} cursor-pointer select-none hover:text-theme-primary`}
                      onClick={() => toggleSort("total")}
                      title="Gross PO commitment (header total — incl. capital & re-issues — not operating spend). Sort by total."
                    >
                      <span className="inline-flex items-center gap-1">
                        Gross PO ⤵{sortArrow("total")}
                        <InfoTip term="commitment" />
                      </span>
                    </th>
                    <th scope="col" className={THR}>
                      <span className="inline-flex items-center gap-1">
                        Remaining <InfoTip term="encumbrance" />
                      </span>
                    </th>
                    <th scope="col" className={TH}>
                      Status
                    </th>
                    <th
                      scope="col"
                      className={`${TH} cursor-pointer select-none hover:text-theme-primary`}
                      onClick={() => toggleSort("order_date")}
                      title="Sort by order date"
                    >
                      Ordered{sortArrow("order_date")}
                    </th>
                    <th scope="col" className={`${TH} text-center`}>
                      FY
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {regLoading
                    ? Array.from({ length: 10 }).map((_, i) => (
                        <tr
                          key={i}
                          className="hover:bg-theme-overlay/50 transition-colors"
                        >
                          {Array.from({ length: 10 }).map((__, j) => (
                            <td key={j} className="px-3 py-2.5">
                              <div
                                className="skeleton h-4 rounded-md"
                                style={{
                                  width: `${40 + ((i * 7 + j * 11) % 60)}px`,
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    : rows.map((r) => (
                        <tr
                          key={r.po_number}
                          className="hover:bg-theme-overlay/50 transition-colors"
                        >
                          <td className="px-3 py-2.5 text-sm font-mono text-theme-primary whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span>{r.po_number}</span>
                              {r.is_capital && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-dcvfd-accent/15 text-dcvfd-accent"
                                  title="Capital outlay (apparatus/vehicle/equipment) — one-time, separately budgeted. Not operating spend."
                                >
                                  Capital
                                </span>
                              )}
                              {r.is_reissue && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-status-red/15 text-status-red"
                                  title="Re-issued / possible duplicate: same supplier, amount, and description appears under another PO# (one Open, one Closed). Counted once in gross totals."
                                >
                                  Re-issued
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            className={`${TD} max-w-[200px] truncate`}
                            title={r.supplier ?? ""}
                          >
                            {r.supplier ?? "—"}
                          </td>
                          <td
                            className={`${TD} max-w-[240px] truncate`}
                            title={r.description ?? ""}
                          >
                            {r.description ?? "—"}
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            {r.committee ?? "—"}
                          </td>
                          <td
                            className={`${TD} max-w-[180px] truncate`}
                            title={r.object_desc}
                          >
                            {r.object_desc || "—"}
                          </td>
                          <td className={`${TDR} text-theme-primary`}>
                            {usd0(asNum(r.total))}
                          </td>
                          <td className={`${TDR} font-semibold`}>
                            {r.remaining != null && asNum(r.remaining) > 0 ? (
                              <span className="text-status-amber">
                                {usd0(asNum(r.remaining))}
                              </span>
                            ) : (
                              <span className="text-theme-muted">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {statusBadge(r.status)}
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            {r.order_date ?? "—"}
                          </td>
                          <td className={`${TD} text-center`}>
                            {r.fiscal_year ?? "—"}
                          </td>
                        </tr>
                      ))}
                  {!regLoading && rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-3 py-8 text-center text-sm text-theme-muted"
                      >
                        No purchase orders match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-3 mt-4">
                <span className="text-xs text-theme-muted">
                  Page {register?.page ?? page} of {totalPages} ·{" "}
                  {totalRows.toLocaleString()} total
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={(register?.page ?? page) <= 1}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-theme-overlay text-theme-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-theme-overlay/70"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={(register?.page ?? page) >= totalPages}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-theme-overlay text-theme-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-theme-overlay/70"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </ChartCard>

          <p className="text-xs text-theme-muted">
            Levy fund (PWC appropriation — Oracle/Mobius). Headline figures tie
            to Oracle and are scoped to the {opFy} operating fund {opFund}:{" "}
            <strong>Open Encumbrance</strong> = SUM(remaining) over fund-
            {opFund} open POs (remaining &gt; 0), split current-FY vs
            carried-onto-{opFund} (carryforward flag), matching the Levy
            dashboard. Fund-2101 carryover POs (own swept appropriation) are
            reported separately and are never added into that headline.{" "}
            <strong>Operating Spent / Budget</strong> come from the
            budgetary-control balances (fund {opFund} expenditure + reserve
            objects — the budget equals the county base appropriation), not from
            PO totals. The register&apos;s <strong>Gross PO</strong> column is
            the PO header commitment (incl. capital outlays &amp; re-issued
            duplicates) — <em>not</em> operating spend; it is never summed into
            a headline. &quot;Capital&quot; tags one-time
            apparatus/vehicle/equipment POs; &quot;Re-issued&quot; flags
            duplicate pairs (same supplier + amount + description across an Open
            and a Closed PO#). Status &quot;Closed&quot; groups every non-open
            status (Closed, Finally Closed, Canceled, etc.).
          </p>
        </>
      )}
    </PageShell>
  );
}

// ── Close Worklist ────────────────────────────────────────────────────────────
// The FY-close run-down surface. Open POs (remaining > 0) for the current FY,
// straight from dash_finance_open_po — so SUM(remaining) here equals the page's
// validated Open Encumbrance KPI. Read-only this pass (no disposition writes;
// see fast-follow note). Carryover vs current-FY is flagged. POs must clear
// before fiscal close (June 30), behind the year-end P-Card allocation
// deadline (June 24 — a fixed PWC calendar date shown on the countdown banner).
function CloseWorklist({
  rows,
  isLoading,
  operatingTotal,
  operatingCount,
  carryTotal,
  carryCount,
  kpiTotal,
  operatingFund,
  priorFyLabel,
  registerPending,
  newFySuffix,
  onSort,
  arrow,
}: {
  rows: OpenPoRow[];
  isLoading: boolean;
  operatingTotal: number;
  operatingCount: number;
  carryTotal: number;
  carryCount: number;
  kpiTotal: number;
  operatingFund: string;
  priorFyLabel: string;
  registerPending: boolean;
  newFySuffix: string;
  onSort: (col: "remaining" | "budget_date") => void;
  arrow: (col: "remaining" | "budget_date") => string;
}) {
  // Reconciliation check: the OPERATING-FUND subtotal vs the Open Encumbrance
  // KPI (allow $1 of float slack). This is a real independent tie — the KPI is
  // Oracle-tied while the subtotal is summed client-side over the open-PO rows.
  // Never compare the all-rows total to itself and call it "Tied": the fund-2101
  // carryover pool is deliberately outside the KPI and is shown separately.
  const reconciles = Math.abs(operatingTotal - kpiTotal) < 1;
  const hasCarryPool = carryCount > 0;
  const subtitleParts = [
    `Operating fund ${operatingFund} (${operatingCount} PO${operatingCount === 1 ? "" : "s"} · ${usd0(operatingTotal)})`,
    ...(hasCarryPool
      ? [
          `${priorFyLabel} carryover fund 2101 (${carryCount} PO${carryCount === 1 ? "" : "s"} · ${usd0(carryTotal)} — own appropriation)`,
        ]
      : []),
  ];
  return (
    <>
      <CloseCountdownBanner />

      {/* Worklist summary chips — one per rundown pool + the independent tie. */}
      <div
        className={`grid grid-cols-1 gap-4 ${hasCarryPool ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
      >
        <KPICard
          label={`Operating Fund ${operatingFund}`}
          value={usd0(operatingTotal)}
          subtitle={`${operatingCount} open PO${operatingCount === 1 ? "" : "s"} · the pool the Open Encumbrance KPI measures`}
          accent="amber"
          isLoading={isLoading}
        />
        {hasCarryPool && (
          <KPICard
            label={`${priorFyLabel} Carryover (Fund 2101)`}
            value={usd0(carryTotal)}
            subtitle={`${carryCount} PO${carryCount === 1 ? "" : "s"} · own swept appropriation — separate pool, not in the KPI`}
            accent="neutral"
            isLoading={isLoading}
          />
        )}
        <KPICard
          label="Reconciles to KPI"
          value={reconciles ? "✓ Tied" : "Δ Check"}
          subtitle={`Fund-${operatingFund} subtotal vs Open Encumbrance KPI ${usd0(kpiTotal)} (Oracle-tied)`}
          accent={reconciles ? "green" : "red"}
          isLoading={isLoading}
        />
      </div>

      <ChartCard
        title="PO Close Worklist"
        subtitle={`Open POs (remaining > 0) across both rundown pools: ${subtitleParts.join(" + ")}.`}
        empty={!isLoading && rows.length === 0}
        minHeight={120}
        headerRight={
          <ExportCsvButton
            filename="dcvfd-po-close-worklist"
            rows={rows}
            columns={WORKLIST_CSV_COLUMNS}
          />
        }
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5 max-h-[640px] overflow-y-auto">
          <table className="w-full min-w-[940px] text-left">
            <caption className="sr-only">Purchase order close worklist</caption>
            <thead className="sticky top-0 z-10 bg-theme-surface">
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  PO #
                </th>
                <th scope="col" className={TH}>
                  Supplier
                </th>
                <th scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Object <InfoTip term="object" />
                  </span>
                </th>
                <th
                  scope="col"
                  className={`${THR} cursor-pointer select-none hover:text-theme-primary`}
                  onClick={() => onSort("remaining")}
                  title="Sort by remaining"
                >
                  Remaining{arrow("remaining")}
                </th>
                <th
                  scope="col"
                  className={`${TH} cursor-pointer select-none hover:text-theme-primary`}
                  onClick={() => onSort("budget_date")}
                  title="Sort by budget date"
                >
                  Budget Date{arrow("budget_date")}
                </th>
                <th scope="col" className={TH}>
                  Status
                </th>
                <th scope="col" className={`${TH} text-center`}>
                  FY
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <tr
                      key={i}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-3 py-2.5">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${40 + ((i * 7 + j * 11) % 60)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.map((r) => {
                    const isCarry = asNum(r.carryover) === 1;
                    const fy = r.fy_established
                      ? `FY${String(r.fy_established).slice(2)}`
                      : "—";
                    // Split-funded POs appear once per fund slice — key by
                    // PO# + fund or React drops/duplicates rows on re-render.
                    const isCarryFund =
                      operatingFund !== "2101" && r.fund === "2101";
                    return (
                      <tr
                        key={`${r.po_number}-${r.fund ?? "?"}`}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className="px-3 py-2.5 text-sm font-mono text-theme-primary whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            {r.po_number}
                            {isCarryFund && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-theme-overlay text-theme-secondary border border-theme-subtle"
                                title={`Fund 2101 — ${priorFyLabel} carryover riding its own swept appropriation. Not part of the fund-${operatingFund} Open Encumbrance KPI.`}
                              >
                                Fund 2101
                              </span>
                            )}
                          </span>
                        </td>
                        <td
                          className={`${TD} max-w-[220px] truncate`}
                          title={r.supplier || r.description || ""}
                        >
                          {r.supplier || r.description || "—"}
                        </td>
                        <td
                          className={`${TD} max-w-[200px] truncate`}
                          title={r.object_desc}
                        >
                          <span className="font-mono text-theme-muted">
                            {r.object_segment}
                          </span>{" "}
                          {r.object_desc}
                        </td>
                        <td
                          className={`${TDR} font-semibold text-status-amber`}
                        >
                          {usd0(asNum(r.remaining))}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {r.budget_date || "—"}
                        </td>
                        <td className="px-3 py-2.5">{statusBadge(r.status)}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">
                          {isCarry ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-status-red/15 text-status-red"
                              title="Carried over from a prior fiscal year"
                            >
                              ↺ {fy}
                            </span>
                          ) : (
                            <span className="text-xs text-theme-muted">
                              {fy}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-sm text-theme-muted"
                  >
                    No open purchase orders. Nothing left to run down.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {registerPending && (
        <p className="text-xs text-theme-muted">
          Supplier/committee attribution for new -{newFySuffix} POs arrives with
          the next PO-register refresh (register as of ~Jul 6) — until then
          those rows show “—”.
        </p>
      )}

      <p className="text-xs text-theme-muted">
        The close worklist lists every open PO (remaining &gt; 0) from{" "}
        <strong>dash_finance_open_po</strong> — the same validated source behind
        the <strong>Open Encumbrance</strong> KPI. It covers BOTH rundown pools:
        the fund-{operatingFund} operating subtotal ties to that headline KPI,
        while fund-2101 carryover POs (tagged{" "}
        <span className="font-semibold">Fund 2101</span>) ride their own swept
        appropriation and are subtotaled separately — the two are never blended.
        These POs must clear before fiscal close (June 30), which sits behind
        the PWC year-end P-Card allocation deadline (June 24, shown on the
        countdown banner above). The{" "}
        <span className="text-status-red font-semibold">↺</span> flag marks
        carryover POs (established in a prior FY); unflagged rows are
        current-FY. Read-only this pass — disposition tagging (clear / keep /
        cancel) is a fast-follow.
      </p>
    </>
  );
}
